import {
  Box,
  CloseIcon,
  Column,
  Heading,
  IconButton,
  Row,
  Skeleton,
  SkeletonBox,
  Tab,
  TabList,
  Tabs,
} from "@hightouch/htui";

import { INBOX_TABS, type InboxTab } from "src/pages/projects/inbox/inbox-tabs";

const SKELETON_ROWS = ["a", "b", "c", "d", "e"];

const TAB_LABELS: Record<InboxTab, string> = {
  forYou: "For you",
  activity: "Activity",
};

type InboxHeaderProps = {
  tab?: InboxTab;
  /** Unread rows on each tab, shown once the inbox has loaded. */
  counts?: Record<InboxTab, number>;
  onTabChange?: (tab: InboxTab) => void;
  onClose: () => void;
  /**
   * Holds the header's place while the inbox loads.
   * Nothing in it takes tab focus, so the view opening over the page focuses itself rather than a control that unmounts as the inbox replaces the skeleton.
   */
  isPlaceholder?: boolean;
};

/** The inbox's title, its tabs and its close button over the page, on one line. */
export const InboxHeader = ({
  tab = "forYou",
  counts,
  onTabChange,
  onClose,
  isPlaceholder = false,
}: InboxHeaderProps) => (
  <Row
    align="flex-end"
    gap={8}
    pl={6}
    pr={4}
    flexShrink={0}
    borderBottomWidth="hairline"
    borderColor="base.divider"
    // The header draws the line under the tabs, so the tab list's own would double it.
    sx={{ "& [role=tablist]": { borderBottomWidth: 0 } }}
  >
    <Box pt={4} pb={3}>
      <Heading size="xl">Inbox</Heading>
    </Box>
    <Box flex={1} minWidth={0}>
      <Tabs
        index={INBOX_TABS.indexOf(tab)}
        onChange={(index) => {
          const next = INBOX_TABS[index];
          if (next) onTabChange?.(next);
        }}
      >
        <TabList>
          {INBOX_TABS.map((id) => (
            <Tab
              key={id}
              count={counts?.[id] || undefined}
              badgeVariant={id === "forYou" ? "info" : "subtle"}
              isDisabled={isPlaceholder}
            >
              {TAB_LABELS[id]}
            </Tab>
          ))}
        </TabList>
      </Tabs>
    </Box>
    <Box pb={2}>
      <IconButton
        aria-label="Close inbox"
        icon={CloseIcon}
        variant="tertiary"
        tabIndex={isPlaceholder ? -1 : undefined}
        onClick={onClose}
      />
    </Box>
  </Row>
);

/** The inbox's layout with placeholder rows, cheap enough to show while the inbox opens over the page. */
export const InboxSkeleton = ({
  onClose,
}: Pick<InboxHeaderProps, "onClose">) => (
  <Column flex={1} minHeight={0}>
    <InboxHeader onClose={onClose} isPlaceholder />
    <Box
      display="grid"
      gridTemplateColumns={{ base: "1fr", lg: "400px minmax(0, 1fr)" }}
      // Rows the height of the view, not of their content, so the list and the open request each scroll inside it.
      gridTemplateRows={{
        base: "repeat(2, minmax(0, 1fr))",
        lg: "minmax(0, 1fr)",
      }}
      flex={1}
      minHeight={0}
    >
      <Column
        minHeight={0}
        pt={3}
        borderRightWidth={{ lg: "hairline" }}
        borderColor="base.divider"
      >
        <FeedSkeleton />
      </Column>
    </Box>
  </Column>
);

export const FeedSkeleton = () => (
  <Column gap={1}>
    {SKELETON_ROWS.map((key) => (
      <FeedSkeletonRow key={key} />
    ))}
  </Column>
);

export const FeedSkeletonRow = () => (
  <Skeleton isLoading>
    <Row gap={3} py={3} px={5} align="flex-start">
      <SkeletonBox boxSize={8} borderRadius="full" />
      <Column flex={1} gap={2} pt={1}>
        <SkeletonBox height={3} width="70%" borderRadius="sm" />
        <SkeletonBox height={3} width="50%" borderRadius="sm" />
        <SkeletonBox height={4} width={20} borderRadius="md" />
      </Column>
    </Row>
  </Skeleton>
);
